import {
  BehaviorSubject,
  Observable,
  distinctUntilChanged,
  map,
  tap,
} from 'rxjs';
import {
  Component,
  DestroyRef,
  Input,
  ViewChild,
  computed,
  effect,
  inject,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { Store, select } from '@ngrx/store';
import { deleteAlertSuccess } from 'src/app/core/actions/feed.actions';
import { selectFeed } from 'src/app/core/reducers';
import {
  injectDeleteAlertMutation,
  injectFeedAlertsQuery,
} from 'src/app/api/queries/alert.queries';

import { Alert } from 'src/app/core/models/alert.models';
import { AlertEditorComponent } from '../../alert-editor/alert-editor.component';
import { AppState } from 'src/app/reducers';
import { Feed } from 'src/app/core/models/core.models';
import { ToolbarButton } from '../../../models/toolbar.models';
import { TranslateService } from '@ngx-translate/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ConfirmDialogComponent } from '../../popups/confirm-dialog/confirm-dialog.component';
import {
  DialogCloseOptions,
  DialogCloseReason,
  DialogService,
} from '@sui/dialog';

@Component({
  selector: 'app-feed-alerts',
  templateUrl: './feed-alerts.component.html',
  styleUrls: ['./feed-alerts.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class FeedAlertsComponent {
  @ViewChild('alertEditor', { static: false })
  alertEditor!: AlertEditorComponent;
  searchString: string;

  private readonly feedIdSignal = signal<string | null>(null);
  private readonly alertsQuery = injectFeedAlertsQuery(this.feedIdSignal);
  readonly alerts = computed<Alert[]>(() => this.alertsQuery.data() ?? []);
  readonly alertsLoading = computed(() => this.alertsQuery.isFetching());
  private readonly deleteAlertMutation = injectDeleteAlertMutation();
  editMode: boolean = false;
  createMode: boolean = false;
  alertsCount: any;
  feed$: Observable<Feed>;
  feedId: string;
  loading: boolean = false;
  selectedAlert: Alert;

  buttons$ = new BehaviorSubject<ToolbarButton[]>([]);

  protected destroyRef = inject(DestroyRef);
  private dialogService = inject(DialogService);

  constructor(
    private store: Store<AppState>,
    private translateService: TranslateService,
  ) {
    this.feed$ = this.store.pipe(select(selectFeed));

    effect(() => (this.alertsCount = this.alerts().length));
  }

  ngOnInit() {
    // Keep feedId — and with it the alerts query key — in sync.
    this.feed$
      .pipe(
        map((feed) => feed?.id),
        distinctUntilChanged(),
        tap((id) => {
          this.feedId = id;
          this.feedIdSignal.set(id ?? null);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.updateToolbarButtons();
  }

  /**
   * The alert editor's save does not go through the form slice, so its
   * `saving$` / `success$` streams never fire here. It emits these two instead;
   * without them the panel would never leave edit mode.
   */
  onEditorSaving(saving: boolean): void {
    this.loading = saving;
    this.updateToolbarButtons();
  }

  onEditorSaved(): void {
    this.editMode = false;
    this.createMode = false;
    this.updateToolbarButtons();
  }

  updateToolbarButtons() {
    const buttons: ToolbarButton[] = [
      {
        type: 'button',
        variant: 'primary',
        label: this.translateService.instant('feed-alerts.button.create-alert'),
        action: this.openAlertCreate.bind(this),
        loading: this.loading,
        shouldDisplay: !this.editMode && !this.createMode,
      },
      {
        type: 'button',
        variant: 'ghost',
        label: this.translateService.instant('btn.cancel'),
        action: () => {
          this.editMode = false;
          this.createMode = false;
          this.updateToolbarButtons();
        },
        shouldDisplay: this.editMode || this.createMode,
      },
      {
        type: 'button',
        variant: 'primary',
        label: this.translateService.instant('btn.save'),
        action: this.alertEdit.bind(this),
        loading: this.loading,
        shouldDisplay: this.editMode,
      },
      {
        type: 'button',
        variant: 'primary',
        label: this.translateService.instant('btn.create'),
        action: this.alertCreate.bind(this),
        shouldDisplay: this.createMode,
      },
    ];

    this.buttons$.next(buttons);
  }

  openAlertEdit(alert: Alert) {
    this.editMode = true;
    this.selectedAlert = alert;
    this.updateToolbarButtons();
  }

  openAlertCreate() {
    this.createMode = true;
    this.updateToolbarButtons();
  }

  alertCreate() {
    this.alertEditor.submit();
    this.updateToolbarButtons();
  }

  alertEdit() {
    this.alertEditor.submit();
    this.updateToolbarButtons();
  }

  alertDelete(alert: Alert) {
    const title = this.translateService.instant(
      'feed-alerts.alert-delete-confirmation-title',
    );

    const message = this.translateService.instant(
      'feed-alerts.alert-delete-confirmation',
    );

    const dialogRef = this.dialogService.openSimpleDialog(
      ConfirmDialogComponent,
      {
        data: {
          title,
          message,
        },
        closeOnClick: false,
      },
    );

    dialogRef.closed.subscribe(
      (event: DialogCloseOptions<{ reason: DialogCloseReason }>) => {
        if (event.reason === DialogCloseReason.Confirm) {
          // * `alertsCount` is still NgRx - keep the broadcast.
          // * `mutateAsync().then()` rather than `mutate(…, {onSuccess})`: a
          // * per-call callback is dropped if this panel closes mid-request.
          // * The promise is not.
          this.deleteAlertMutation
            .mutateAsync(alert.id)
            .then(() =>
              this.store.dispatch(deleteAlertSuccess({ alertId: alert.id })),
            )
            .catch(() => undefined);
        } else if (event.reason === DialogCloseReason.Cancel) {
          console.log(`Action cancelled: ${event.reason}`);
        }
      },
    );
  }
}
